import { Button } from '@/components/ui/button';
import { ThemedText } from '@/components/themed-text';
import { Ionicons } from '@expo/vector-icons';
import { useAuthStore } from '@/store/authStore';
import api from '@/services/api';
import { useQueryClient } from '@tanstack/react-query';
import { router, useLocalSearchParams } from 'expo-router';
import React, { useEffect, useState } from 'react';
import { ActivityIndicator, Platform, View } from 'react-native';
import { isWebReturnToAppFlow } from '@/lib/auth/web-onboarding-return-to-app';
import { resolveMembership } from '@/hooks/use-membership';

/** Quanto esperar o plano de equipe aparecer no `/user/me` (webhook). */
const TEAM_CONFIRM_INTERVAL_MS = 2000;
const TEAM_CONFIRM_ATTEMPTS = 30;

export default function SubscriptionSuccessScreen() {
  const [loading, setLoading] = useState(false);
  const fetchProfile = useAuthStore((s) => s.fetchProfile);
  const queryClient = useQueryClient();
  const { session_id: sessionId } = useLocalSearchParams<{ session_id?: string }>();
  // Organização que acabou de pagar o plano de equipe: quem libera é o
  // backend (confirmação do checkout ou webhook); aqui o app confere no
  // `/user/me` até a pendência sumir, senão a guarda do layout o devolveria
  // para os planos.
  const teamPlanPending = useAuthStore(
    (s) => resolveMembership(s.user?.membership).teamPlanPending,
  );
  const [confirmTimedOut, setConfirmTimedOut] = useState(false);

  // Aplica a assinatura sem esperar o webhook; falha aqui não trava nada,
  // o webhook ainda chega depois.
  useEffect(() => {
    if (!sessionId) return;
    api
      .post('/stripe/checkout-session/confirm', { sessionId })
      .then(() => fetchProfile())
      .then(() => queryClient.invalidateQueries())
      .catch(() => {});
  }, [sessionId, fetchProfile, queryClient]);

  useEffect(() => {
    if (!teamPlanPending) return;
    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      if (attempts > TEAM_CONFIRM_ATTEMPTS) {
        clearInterval(timer);
        setConfirmTimedOut(true);
        return;
      }
      void Promise.resolve(fetchProfile()).catch(() => {});
    }, TEAM_CONFIRM_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [teamPlanPending, fetchProfile]);

  const handleGoToDashboard = async () => {
    setLoading(true);
    try {
      await fetchProfile();
      if (Platform.OS === 'web' && isWebReturnToAppFlow()) {
        router.replace('/subscription/plan-selected');
        return;
      }
      router.replace('/(tabs)/(provider)/home');
    } catch {
      router.replace('/(tabs)/(provider)/home');
    } finally {
      setLoading(false);
    }
  };

  return (
    <View className="flex-1 items-center justify-center bg-canvas p-8">
      <View className="mb-6 rounded-full bg-[#FD5001]/10 p-6">
        <Ionicons name="checkmark-circle" size={64} color="#FD5001" />
      </View>
      <ThemedText className="text-center font-bold text-2xl">
        {teamPlanPending ? 'Confirmando o pagamento…' : 'Assinatura ativada!'}
      </ThemedText>
      <ThemedText className="mt-2 text-center text-gray-600">
        {!teamPlanPending
          ? 'Sua assinatura foi confirmada. Agora você tem acesso completo à Controlapag.'
          : confirmTimedOut
            ? 'O pagamento ainda não foi confirmado. Se foi aprovado, a equipe libera em alguns minutos.'
            : 'Assim que o pagamento for confirmado, a equipe fica liberada.'}
      </ThemedText>
      <Button
        className="mt-8"
        onPress={handleGoToDashboard}
        disabled={loading || (teamPlanPending && !confirmTimedOut)}
      >
        {loading || (teamPlanPending && !confirmTimedOut) ? (
          <ActivityIndicator size="small" color="white" />
        ) : (
          'Ir para o dashboard'
        )}
      </Button>
    </View>
  );
}
